import { useCallback, useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { ArrowLeft, RefreshCw, Settings } from "lucide-react";
import { xeroApi } from "../../backend/api/xero";
import type { Integration } from "../../backend/api/xero";
import { Alert, AlertDescription, AlertTitle } from "../../components/ui/alert";
import { Button } from "../../components/ui/button";
import { Card, CardContent } from "../../components/ui/card";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../../components/ui/table";
import { StatusBadge, formatDateTime, triggeredBy } from "./shared";

/** Reads as "01:00, 13:00 AEST", or says plainly that nothing is scheduled. */
const describeSchedule = (schedule: Integration["schedule"]) => {
  if (!schedule || !schedule.enabled || schedule.run_times.length === 0) {
    return "Not scheduled";
  }
  return `${schedule.run_times.join(", ")} ${
    schedule.timezone === "Australia/Brisbane" ? "AEST" : schedule.timezone
  }`;
};

const capabilityName = (integration: Integration) =>
  integration.id === "invoice-sync" ? "Invoices" : integration.name;

/** The syncs that run against Xero. The connection itself lives under settings. */
export default function XeroIntegration() {
  const navigate = useNavigate();

  const [integrations, setIntegrations] = useState<Integration[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  const load = useCallback(async (quiet = false) => {
    if (!quiet) setLoading(true);
    const { data, error: loadError } = await xeroApi.listIntegrations();
    if (!quiet) setLoading(false);

    if (loadError) {
      setError(loadError.message || "Could not load the Xero syncs.");
      return;
    }

    setError("");
    setIntegrations(data || []);
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  // A sync started from anywhere, including its own page or the scheduler,
  // changes status without this page asking. Polling stops as soon as nothing
  // is in flight, so an idle list is not sitting on a timer.
  const inFlight = integrations.some(
    (integration) =>
      integration.last_run?.status === "queued" ||
      integration.last_run?.status === "running",
  );

  useEffect(() => {
    if (!inFlight) return undefined;
    const timer = window.setInterval(() => load(true), 2000);
    return () => window.clearInterval(timer);
  }, [inFlight, load]);

  return (
    <main className="mx-auto flex max-w-[1500px] flex-col gap-5 p-4 sm:p-6">
      <Link
        to="/integrations"
        className="inline-flex w-fit items-center gap-1 text-sm text-muted-foreground hover:text-foreground"
      >
        <ArrowLeft className="h-4 w-4" />
        Connections
      </Link>

      <div className="flex flex-col gap-3 sm:flex-row sm:items-end sm:justify-between">
        <h1 className="text-2xl font-semibold">Xero</h1>
        <div className="flex items-center gap-2">
          <Button
            variant="outline"
            size="icon"
            onClick={() => load()}
            disabled={loading}
            aria-label="Refresh Xero"
            title="Refresh Xero"
          >
            <RefreshCw className={`h-4 w-4 ${loading ? "animate-spin" : ""}`} />
          </Button>
          <Button asChild>
            <Link to="/integrations/xero/settings">
              <Settings className="h-4 w-4" />
              Connection settings
            </Link>
          </Button>
        </div>
      </div>

      {error && (
        <Alert variant="destructive">
          <AlertTitle>Something went wrong</AlertTitle>
          <AlertDescription>{error}</AlertDescription>
        </Alert>
      )}

      <Card>
        <CardContent className="pt-6">
          <div className="overflow-x-auto rounded-md border">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Data</TableHead>
                  <TableHead>Schedule (AEST)</TableHead>
                  <TableHead>Last run</TableHead>
                  <TableHead>Triggered by</TableHead>
                  <TableHead>Status</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {loading ? (
                  <TableRow>
                    <TableCell colSpan={5} className="h-24 text-center text-muted-foreground">
                      Loading...
                    </TableCell>
                  </TableRow>
                ) : (
                  integrations.map((integration) => (
                    <TableRow
                      key={integration.id}
                      className="cursor-pointer hover:bg-muted/50"
                      onClick={() => navigate(`/integrations/xero/syncs/${integration.id}`)}
                    >
                      <TableCell className="font-medium">
                        {capabilityName(integration)}
                      </TableCell>
                      <TableCell className="whitespace-nowrap">
                        {describeSchedule(integration.schedule)}
                      </TableCell>
                      <TableCell className="whitespace-nowrap">
                        {integration.last_run
                          ? formatDateTime(integration.last_run.started_at)
                          : "Never run"}
                      </TableCell>
                      <TableCell className="whitespace-nowrap">
                        {integration.last_run ? triggeredBy(integration.last_run) : "-"}
                      </TableCell>
                      <TableCell>
                        {integration.last_run ? (
                          <StatusBadge status={integration.last_run.status} />
                        ) : (
                          <span className="text-muted-foreground">-</span>
                        )}
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </div>
        </CardContent>
      </Card>
    </main>
  );
}
